import "./list-employee-shift.scss";

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import SearchInput from "@shared/ui/search-input/search-input";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useQyGetEmployeeShift } from "@core/query/employee-shift.query";
import { GetEmployeeShiftModel } from "@core/model/query-model-employee-shift";
import { TableSort } from "@core/model/table-sort";
import { FilterEmployeeShift } from "../filter-employee-shift/filter-employee-shift";
import { useFilterEmployeeShiftContext } from "../filter-employee-shift/filter-employee-shift.context";
import { GetEmployeeShiftDto } from "@api/api";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function EmployeeShift() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const { formatTime, getTime } = useDateTimeContext();
  const { personId } = useParams();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, filterEntity } = useFilterEmployeeShiftContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: employeeShiftResponse,
    isLoading,
    isError,
    error,
  } = useQyGetEmployeeShift(searchTerm, rowLimit, pageNumber, sort, {
    ...filterEntity,
    user_code: personId || "",
  });

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetEmployeeShiftModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        {/* <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        /> */}
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterEmployeeShift />
      </Sidebar>
    </section>
  );
  const displayShiftCol = (cell: GetEmployeeShiftDto) => {
    return cell.shift ? (
      <span>
        {cell?.shift.title}: ({cell.shift?.shift_type_name}){" "}
        {formatTime(getTime(cell.shift?.start))}-
        {formatTime(getTime(cell.shift?.end))}
      </span>
    ) : (
      "-"
    );
  };
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{employeeShiftResponse?.count}</b>
      </h5>

      <DataTable
        value={employeeShiftResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column header="Shift" body={displayShiftCol} sortable></Column>
        <Column
          field="effectivity_date"
          header="Effectivity Date"
          sortable
        ></Column>
        <Column field="end_date" header="End Date" sortable></Column>
        <Column field="day" header="Day" sortable></Column>
        <Column field="is_permanent" header="Is Permanent" sortable></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={employeeShiftResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="EmployeeShift" className="employee-shift">
      <HeaderContent title="Employee Shift" headingType="h2">
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default EmployeeShift;
